import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Camera, ArrowUpRight, Lock, UploadCloud } from "lucide-react";
import { waLink, REVIEWS } from "@/data/site";
import { Reveal, SectionHead, useLang } from "./common";
import VisitTracker from "./VisitTracker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SHOTS = ["roof", "hero", "wet", "install", "clean", "tech"];
const MAX_PHOTOS = 6;
const MAX_SIZE = 5 * 1024 * 1024;
const OK_TYPES = ["image/jpeg", "image/png", "image/webp"];

const ReviewCard = ({ r, labels }) => (
  <Reveal className="rounded-[2rem] bg-white border border-[#0F295B]/10 p-7 flex flex-col" delay={0.05}>
    <div data-testid="review-card" className="flex-1">
      <div className="flex items-center gap-1 mb-4">
        {Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="w-4 h-4 text-[#F39C12]" fill="currentColor" />)}
      </div>
      <p className="text-[#0F295B] leading-relaxed">“{r.text}”</p>
    </div>
    <div className="mt-6 flex items-center justify-between gap-3">
      <div>
        <p className="font-bold text-[#0F295B]">{r.name}</p>
        <p className="text-xs text-[#4A5B78]">{r.area}</p>
      </div>
      {r.sample && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#F39C12]/15 text-[#B26A00] border border-[#F39C12]/40 px-2.5 py-1">{labels.sample}</span>}
    </div>
  </Reveal>
);

const OwnerUpload = ({ onUploaded }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [files, setFiles] = useState([]);
  const [capEn, setCapEn] = useState("");
  const [capHi, setCapHi] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const pick = (e) => {
    const picked = Array.from(e.target.files || []).filter((p) => OK_TYPES.includes(p.type) && p.size <= MAX_SIZE);
    setFiles(picked.slice(0, MAX_PHOTOS));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !files.length) return;
    setBusy(true);
    setStatus(null);
    const paths = [];
    for (const p of files) {
      try {
        const fd = new FormData();
        fd.append("file", p);
        const r = await fetch(`${BACKEND}/api/upload`, { method: "POST", body: fd });
        const d = await r.json();
        if (d.path) paths.push(d.path);
      } catch { /* continue */ }
    }
    try {
      const r = await fetch(`${BACKEND}/api/gallery/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, paths, caption_en: capEn, caption_hi: capHi }),
      });
      if (!r.ok) throw new Error();
      setStatus("ok");
      setFiles([]);
      setCapEn("");
      setCapHi("");
      onUploaded();
    } catch {
      setStatus("err");
    }
    setBusy(false);
  };

  const inp = "w-full rounded-2xl bg-[#F8F9FA] border border-[#0F295B]/15 px-3.5 py-2.5 text-sm text-[#0F295B] placeholder:text-[#6B7A93] focus:outline-none focus:border-[#00A651]";
  return (
    <div className="mt-4">
      <button onClick={() => setOpen((o) => !o)} data-testid="gallery-owner-toggle"
        className="text-xs font-bold text-[#4A5B78] flex items-center gap-1.5 hover:text-[#0F295B] transition-colors">
        <Lock className="w-3.5 h-3.5" /> {t.gallery.ownerToggle}
      </button>
      <AnimatePresence>
        {open && (
          <motion.form onSubmit={submit} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            data-testid="owner-upload-panel" className="mt-3 rounded-3xl bg-white border border-[#0F295B]/10 p-5 space-y-3 overflow-hidden">
            <p className="font-display text-lg text-[#0F295B]">{t.gallery.ownerTitle}</p>
            <input type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t.gallery.codePh} data-testid="owner-code" required className={inp} />
            <div className="grid sm:grid-cols-2 gap-2">
              <input value={capEn} onChange={(e) => setCapEn(e.target.value)} placeholder={t.gallery.capEnLabel} data-testid="owner-caption-en" className={inp} />
              <input value={capHi} onChange={(e) => setCapHi(e.target.value)} placeholder={t.gallery.capHiLabel} data-testid="owner-caption-hi" className={inp} />
            </div>
            <label className="flex items-center justify-between gap-3 rounded-2xl border-2 border-dashed border-[#0F295B]/25 px-4 py-3.5 cursor-pointer hover:border-[#00A651] transition-colors" data-testid="owner-photos">
              <span className="flex items-center gap-2 text-sm font-semibold text-[#0F295B]">
                <UploadCloud className="w-4 h-4 text-[#00A651]" /> {files.length ? `${files.length} ✓` : t.gallery.chooseLabel}
              </span>
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={pick} data-testid="owner-photos-input" />
            </label>
            <button type="submit" disabled={busy || !files.length} data-testid="owner-submit"
              className="w-full rounded-full bg-[#F39C12] text-[#0F295B] font-bold py-3 text-sm hover:bg-[#ffb13b] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              <Camera className="w-4 h-4" /> {t.gallery.submitPhotos}
            </button>
            {busy && <p className="text-xs text-[#4A5B78]">{t.contact.uploading}</p>}
            {status === "ok" && <p data-testid="owner-status" className="text-xs font-bold text-[#00A651]">{t.gallery.uploadOk}</p>}
            {status === "err" && <p data-testid="owner-status" className="text-xs font-bold text-red-600">{t.gallery.uploadErr}</p>}
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Gallery = () => {
  const { t, lang } = useLang();
  const reviews = REVIEWS;
  const [real, setReal] = useState([]);
  const loadPhotos = () => fetch(`${BACKEND}/api/gallery-photos`).then((r) => r.json()).then((d) => setReal(Array.isArray(d) ? d : [])).catch(() => {});
  useEffect(() => {
    loadPhotos();
  }, [lang]);

  const realItems = real.map((p) => ({
    src: `${BACKEND}/api/files/${p.storage_path}`,
    cap: (lang === "hi" ? p.caption_hi || p.caption_en : p.caption_en) || t.gallery.captions[0],
    real: true,
  }));
  const stockItems = SHOTS.map((s, i) => ({ src: `/assets/${s}.jpg`, cap: t.gallery.captions[i], real: false }));
  const items = [...realItems, ...stockItems].slice(0, 6);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 sm:py-32 bg-[#F0F4F8]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionHead num={t.gallery.num} kicker={t.gallery.kicker} segs={t.gallery.title} desc={t.gallery.desc} testid="gallery-head" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <Reveal key={item.src + i} delay={i * 0.05} className={i === 0 ? "col-span-2 aspect-[2/1]" : "aspect-square"}>
              <div data-testid={`gallery-photo-${i}`} className="relative w-full h-full rounded-3xl overflow-hidden group">
                <img src={item.src} alt={item.cap} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.6s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F295B]/70 via-transparent to-transparent opacity-80" />
                <p className="absolute bottom-4 left-4 text-white font-semibold text-sm flex items-center gap-2">
                  {item.cap}
                  {item.real && <span className="text-[9px] font-bold uppercase tracking-wider rounded-full bg-[#00A651] px-2 py-0.5">{lang === "hi" ? "असली" : "Real"}</span>}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <OwnerUpload onUploaded={loadPhotos} />
        <VisitTracker />
        <div className="mt-20">
          <Reveal><h3 data-testid="reviews-title" className="font-display text-2xl sm:text-3xl text-[#0F295B] mb-8">{t.gallery.reviewsTitle}</h3></Reveal>
          {reviews.length ? (
            <div className="grid md:grid-cols-3 gap-4">
              {reviews.map((r, i) => <ReviewCard key={r.name + i} r={r} labels={t.gallery} />)}
            </div>
          ) : (
            <Reveal><p data-testid="reviews-empty" className="text-[#4A5B78]">{t.gallery.empty}</p></Reveal>
          )}
          <Reveal delay={0.1} className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={waLink(lang === "hi" ? "नमस्ते Vivan Energia, मैं अपनी साइट की तस्वीरें और रिव्यू भेजना चाहता/चाहती हूँ।" : "Hi Vivan Energia, I'd like to send my site photos & review.")} target="_blank" rel="noreferrer" data-testid="gallery-cta"
                className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[#0F295B] text-white font-bold py-4 hover:bg-[#00A651] transition-colors">
                <Camera className="w-5 h-5" /> {t.gallery.cta} <ArrowUpRight className="w-5 h-5" />
              </a>
              <a href={waLink(lang === "hi" ? "नमस्ते Vivan Energia, मैं अपना रिव्यू साझा करना चाहता/चाहती हूँ।" : "Hi Vivan Energia, I'd like to share my review.")} target="_blank" rel="noreferrer" data-testid="review-cta"
                className="flex-1 flex items-center justify-center gap-2 rounded-full border-2 border-[#0F295B] text-[#0F295B] font-bold py-4 hover:bg-[#0F295B] hover:text-white transition-colors">
                <Star className="w-5 h-5" /> {t.gallery.reviewCta}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
